import { useQuery } from "@tanstack/react-query";
import { ApiError, SessionChangedError, apiGet } from "../api";

/**
 * Колокольчик шапки: GET /api/notifications (Go считает события из готовых данных —
 * промокоды кончились, кампания остановлена, баланс ниже запаса, оплаченное
 * пополнение, баланс SMSC для УК). Таблиц и отметки «прочитано» нет: список —
 * это текущее состояние, событие пропадает само, когда причина ушла.
 */
export type NotificationKind =
  | "promo_codes_waiting"
  | "campaign_stopped"
  | "low_balance"
  | "topup_paid"
  | "provider_balance_low";

export interface ShellNotification {
  /** Стабильный ключ события (для React key). */
  id: string;
  /** Известный вид или строка из нового Go — тогда значок нейтральный. */
  kind: NotificationKind | string;
  title: string;
  text: string;
  /** ISO-время события. */
  at: string;
  /** Путь SMS-центра или null, если перехода нет. */
  link: string | null;
}

export interface NotificationsData {
  items: ShellNotification[];
  /**
   * Сколько событий требуют действия (Go: всё, кроме «Баланс пополнен»). Это
   * число на колокольчике; справочные пополнения видны в списке, но не в нём.
   */
  count: number;
  /** Код ответа, если сервер не отдал список (старый Go — 404). Показываем пустой список. */
  failed?: number;
}

export const NOTIFICATIONS_KEY = ["marketing", "notifications"] as const;
export const EMPTY_NOTIFICATIONS: NotificationsData = { items: [], count: 0 };

/** Ссылка события — только путь внутри SMS-центра («/campaigns/…»), никаких внешних адресов. */
export function safeInternalLink(link: unknown): string | null {
  if (typeof link !== "string") return null;
  const value = link.trim();
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\"))
    return null;
  return value;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** Разбор ответа без доверия к форме: битые строки отбрасываем, а не роняем шапку. */
export function parseNotifications(raw: unknown): NotificationsData {
  if (!raw || typeof raw !== "object") return EMPTY_NOTIFICATIONS;
  const body = raw as { items?: unknown; count?: unknown };
  const items: ShellNotification[] = [];
  const seen = new Set<string>();
  if (Array.isArray(body.items)) {
    for (const entry of body.items) {
      if (!entry || typeof entry !== "object") continue;
      const row = entry as Record<string, unknown>;
      const id = text(row.id);
      const title = text(row.title);
      if (!id || !title || seen.has(id)) continue;
      seen.add(id);
      items.push({
        id,
        kind: text(row.kind),
        title,
        text: text(row.text),
        at: text(row.at),
        link: safeInternalLink(row.link),
      });
    }
  }
  const count =
    typeof body.count === "number" &&
    Number.isInteger(body.count) &&
    body.count >= 0
      ? body.count
      : items.length;
  return { items, count };
}

/**
 * Запрос колокольчика. Ошибка сервера (404 у старого Go, 5xx, сеть) — пустой
 * список с кодом в `failed`, шапка не падает. Смена сессии во время запроса
 * пробрасывается, чтобы чужие данные не попали в кэш нового входа.
 */
export async function fetchNotifications(
  signal?: AbortSignal,
  get: (path: string, signal?: AbortSignal) => Promise<unknown> = apiGet,
): Promise<NotificationsData> {
  try {
    return parseNotifications(await get("/api/notifications", signal));
  } catch (error) {
    if (error instanceof SessionChangedError) throw error;
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    return {
      ...EMPTY_NOTIFICATIONS,
      failed: error instanceof ApiError ? error.status : 0,
    };
  }
}

/** Обновление раз в минуту; на старом сервере (404) перестаём опрашивать до перезагрузки страницы. */
export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: ({ signal }) => fetchNotifications(signal),
    enabled,
    retry: false,
    staleTime: 30_000,
    refetchInterval: (query) =>
      query.state.data?.failed === 404 ? false : 60_000,
    refetchOnWindowFocus: (query) => query.state.data?.failed !== 404,
  });
}

/** «10.10, 14:05» по Москве; непонятное время — пусто. */
export function notificationTime(at: string): string {
  const date = new Date(at);
  if (!at || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Moscow",
  }).format(date);
}
